// used for working with user's media (camera / microphone)

import type { FacingMode } from "@/enums/FacingMode";

export class MediaService {
  private readonly sendSnapshot: (blob: Blob) => Promise<void>;

  constructor(sendSnapshot: (blob: Blob) => Promise<void>) {
    this.sendSnapshot = sendSnapshot;
  }
  // obtains user's camera and microphone
  async getCameraLocalStream(facingMode: FacingMode): Promise<MediaStream | null> {
    console.log("getCameraLocalStream", facingMode);
    try {
      return await this.getCameraStream(facingMode);
    } catch (error: any) {
      console.error("Failed to get camera stream:", error);
      return null;
    }
  }

  async getCameraStream(facingMode: FacingMode) {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: facingMode.toString().toLowerCase() },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
    } catch (error: any) {
      console.warn("getCameraStream failed, retrying with relaxed constraints", error);
      try {
        return await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
      } catch (retryError: any) {
        console.error("getCameraStream retry also failed", retryError);
        return null;
      }
    }
  }

  async switchLocalCameraStream(currentStream: MediaStream, newFacingMode: FacingMode): Promise<MediaStream | null> {
    const currentVideoTrack = currentStream.getVideoTracks()[0];
    if (currentVideoTrack) {
      currentVideoTrack.stop();
    }

    const newCameraStream = await this.getCameraLocalStream(newFacingMode);

    const oldAudioTrack = currentStream.getAudioTracks()[0];
    if (oldAudioTrack && newCameraStream) {
      newCameraStream.addTrack(oldAudioTrack);
    }

    return newCameraStream;
  }

  // Original code from: https://github.com/mdn/samples-server/blob/master/s/webrtc-capturestill/capture.js



  takeSnapshot2() {
    const video = document.getElementById('localVideo') as HTMLVideoElement | null;
    const canvas = document.createElement("canvas");
    return this.takeSnapshot(video, canvas, true);
  }

  takeSnapshot(video: HTMLVideoElement | null, canvas: HTMLCanvasElement | null, streaming: boolean) {
    if (!canvas || !video) {
      console.error("Canvas or video element is null");
      return;
    }

    const context = canvas.getContext("2d");

    if (!context) {
      console.error("Failed to get canvas context");
      return;
    }

    let height = video.videoHeight;
    let width = video.videoWidth;

    // Firefox currently has a bug where the height can't be read from
    // the video, so we will make assumptions if this happens.
    if (isNaN(height)) {
      height = 1080;
      width = 1920;
    }

    if (width && height) {
      video.setAttribute("width", width.toString());
      video.setAttribute("height", height.toString());
      canvas.setAttribute("width", width.toString());
      canvas.setAttribute("height", height.toString());

      if (!streaming && video.videoHeight === 0 && video.videoWidth === 0) {
        console.error("Cannot take snapshot yet, video is not streaming");
        return;
      }

      setTimeout(() => { // short timeout to wait for canvas
        context!.drawImage(video!, 0, 0, width, height);

        // to blob is used because it has better performance than creating base64 image
        // according to: https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toDataURL
        canvas!.toBlob(this.onBlobCreated.bind(this), 'image/jpeg', 0.85);
      }, 25);
    }
  }

  private async onBlobCreated(blob: Blob | null) {
    if (!blob) {
      console.error("Failed to create Blob from image");
      return;
    }

    await this.sendSnapshot(blob);
  }
}
